/**
 * The Mouja brand unit, set in plain type.
 *
 * **No logo ships with this system and none has been drawn.** The design
 * package is explicit that the brand is typographic — the name in Inter 300
 * with the aqua dot — and that nothing should be reconstructed or approximated
 * to fill the gap. If a real mark is ever supplied it belongs here; until then
 * this is the correct lockup everywhere.
 */

import type { CSSProperties } from 'react';

const SCALE = { sm: 16, md: 20, lg: 28 } as const;

export function Wordmark({
  size = 'md',
  tone = 'auto',
  style,
}: {
  readonly size?: keyof typeof SCALE;
  readonly tone?: 'auto' | 'dark' | 'light';
  readonly style?: CSSProperties;
}): React.ReactElement {
  const scale = SCALE[size];
  const color =
    tone === 'dark'
      ? 'var(--mj-white)'
      : tone === 'light'
        ? 'var(--mj-ink-900)'
        : 'var(--text-primary)';

  return (
    <span
      // The product name is a fixed Latin wordmark, not translated copy. Keep
      // its language, its direction and its characters stable when the
      // surrounding UI is RTL.
      //
      // Four separate guards, because they fail in different places:
      // `.mj-ltr` isolates it in the bidi algorithm, `dir` states the direction
      // for anything that reads the DOM instead, `lang` stops an Arabic font
      // stack claiming it, and `translate="no"` stops a machine translator —
      // in-page or in-browser — from turning the brand into a word.
      className="mj-wordmark mj-ltr"
      lang="en"
      dir="ltr"
      translate="no"
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 1,
        fontFamily: 'var(--font-sans)',
        fontSize: scale,
        fontWeight: 300,
        lineHeight: 1,
        letterSpacing: `${(-0.025 * scale).toFixed(2)}px`,
        color,
        fontFeatureSettings: 'var(--font-feature-default)',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      mouja
      <span style={{ color: 'var(--signal-live)' }}>.</span>
      <span style={{ opacity: 0.62 }}>ai</span>
    </span>
  );
}
